import { FormEvent, useCallback, useContext, useMemo, useState } from "react";
import { Alert, ButtonToolbar, Col, Offcanvas, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import type { RebalanceHistory } from "../../../api/src/rebalances/rebalances.service";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import MobileActionsMenu, {
  MobileActionItem,
} from "../components/MobileActionsMenu";
import { NoteForm, NotesPane } from "../components/Notes";
import EntityAuditTrail from "../components/EntityAuditTrail";
import SummaryPill from "../components/SummaryPill";
import { useAuthenticatedFetch } from "../lib/api";
import { useCreateHouseholdNote, useQueryHouseholdNotes } from "../lib/notes";
import { formatCurrency } from "../lib/numbers";
import { RebalanceContext } from "./RebalanceInfo";
import { useChangeRebalanceState } from "./lib";

const RebalanceHeader = () => {
  const { rebalance, household, isDirty, readOnly } =
    useContext(RebalanceContext);

  const { rebalanceId } = useParams();
  const navigate = useNavigate();

  const updateRebalance = useChangeRebalanceState();

  const notificationContext = useContext(NotificationContext);

  const approve = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      // First, submit the form to save any unsaved trades (only if dirty)
      if (isDirty) {
        const form = document.querySelector("form") as HTMLFormElement;
        if (form) {
          // Wait for form submission to complete
          await new Promise<void>((resolve) => {
            const handleSubmit = () => {
              // Wait for the async submit to complete
              setTimeout(() => {
                form.removeEventListener("submit", handleSubmit);
                resolve();
              }, 100);
            };
            form.addEventListener("submit", handleSubmit);
            form.requestSubmit();
          });
        }
      }

      // Then just change the state (no rebalance data to avoid buggy PATCH save logic)
      await updateRebalance.mutateAsync({
        state: "approved",
        rebalanceIds: [parseInt(rebalanceId ?? "")],
      });

      notificationContext.pushNotification({
        id: `rebalance-${rebalanceId}`,
        header: "Trade Proposal Approved",
        body: `Trade Proposal ${rebalanceId} approved`,
        variant: "success",
      });

      navigate("..");
    },
    [
      navigate,
      notificationContext,
      rebalance,
      rebalanceId,
      updateRebalance,
      isDirty,
    ],
  );

  const reject = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      await updateRebalance.mutateAsync({
        state: "proposed",
        rebalanceIds: [parseInt(rebalanceId ?? "")],
      });

      notificationContext.pushNotification({
        id: `rebalance-${rebalanceId}`,
        header: "Trade Proposal Rejected",
        body: `Trade Proposal ${rebalanceId} rejected`,
        variant: "warning",
      });

      navigate("..");
    },
    [navigate, notificationContext, rebalanceId, updateRebalance],
  );

  const cancel = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      await updateRebalance.mutateAsync({
        state: "canceled",
        rebalanceIds: [parseInt(rebalanceId ?? "")],
      });

      notificationContext.pushNotification({
        id: `rebalance-${rebalanceId}`,
        header: "Trade Proposal Cancelled",
        body: `Trade Proposal ${rebalanceId} canceled`,
        variant: "warning",
      });

      navigate("..");
    },
    [navigate, notificationContext, rebalanceId, updateRebalance],
  );

  const [isShowHistory, setIsShowHistory] = useState(false);
  const {
    isPending: isPendingRebalanceHistory,
    isError: isErrorRebalanceHistory,
    data: dataRebalanceHistory,
  } = useAuthenticatedFetch<
    UnpackResponse<RebalancesController["getRebalanceHistory"]>
  >(`/v1/rebalances/${rebalanceId}/history`);
  const rebalanceHistory = dataRebalanceHistory?.data ?? {
    items: [],
    users: {},
  };

  const showHistory = useCallback(() => {
    setIsShowHistory(true);
  }, []);

  const hideHistory = useCallback(() => {
    setIsShowHistory(false);
  }, []);

  // Each field is `null` when it is NULL or, on every version but the first,
  // the same as the version before (`RebalanceHistory`); a line is shown only
  // for a non-null field.
  const renderRebalanceHistory = useCallback((item: RebalanceHistory) => {
    const results: JSX.Element[] = [];

    if (item.status != null) {
      results.push(<li>Status: {item.status}</li>);
    }

    if (item.dcaStartDate != null) {
      results.push(
        <li>DCA Start Date: {new Date(item.dcaStartDate).toLocaleString()}</li>,
      );
    }

    if (item.dcaEndDate != null) {
      results.push(
        <li>DCA End Date: {new Date(item.dcaEndDate).toLocaleString()}</li>,
      );
    }

    if (item.dcaInterval != null) {
      results.push(<li>DCA Interval: {item.dcaInterval}</li>);
    }

    if (item.dcaBuySell != null) {
      results.push(<li>DCA Buy/Sell: {item.dcaBuySell}</li>);
    }

    if (item.dcaCash != null) {
      results.push(<li>DCA Cash: {item.dcaCash}</li>);
    }

    if (item.proposedExecutedTime != null) {
      results.push(
        <li>
          Proposed Executed Time:{" "}
          {new Date(item.proposedExecutedTime).toLocaleString()}
        </li>,
      );
    }

    if (item.trades.length > 0) {
      results.push(<li>Trades Modified: {item.trades.join(", ")}</li>);
    }

    return <ul className="ps-3">{results}</ul>;
  }, []);

  const trades = rebalance?.accounts.flatMap((account) => account.trades) ?? [];

  const buyTotal = trades
    .filter((trade) => trade.side === "buy")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const sellTotal = trades
    .filter((trade) => trade.side === "sell" || trade.side === "sell_all")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const linkedToWealthbox = household?.idExternal.wealthbox != null;
  const linkedToRedtail = household?.idExternal.redtail != null;
  const linkedToCrm = linkedToWealthbox || linkedToRedtail;

  const [showNotes, setShowNotes] = useState(false);
  const onShowNotes = useCallback(() => {
    setShowNotes(!showNotes);
  }, [showNotes]);

  const householdId = household?.id ?? 0;

  const {
    isPending: isPendingNotes,
    isError: isErrorNotes,
    data: dataNotes,
  } = useQueryHouseholdNotes(householdId);

  const createNote = useCreateHouseholdNote(householdId);

  const onCreateNote = useCallback(
    async (data: NoteForm) => {
      return await createNote.mutateAsync(data);
    },
    [createNote],
  );

  const mobileActions = useMemo(() => {
    const actions: MobileActionItem[] = [];
    // A read-only proposal has no approve, reject or cancel: only its swap
    // changes it (APP-1486).
    if (!readOnly && rebalance?.status === "proposed") {
      actions.push({
        label: "Save & Approve",
        icon: "/icons/approve.svg",
        onClick: approve,
        disabled: updateRebalance.isPending,
      });
    } else if (!readOnly && rebalance?.status === "approved") {
      actions.push({
        label: "Reject",
        icon: "/icons/reject.svg",
        onClick: reject,
        disabled: updateRebalance.isPending,
      });
    }
    if (
      !readOnly &&
      ["requested", "proposed", "approved"].includes(rebalance?.status ?? "")
    ) {
      actions.push({
        label: "Cancel",
        icon: "/icons/trash.svg",
        onClick: cancel,
        disabled: updateRebalance.isPending,
      });
    }
    actions.push({
      label: "History",
      icon: "/icons/audit.svg",
      onClick: showHistory,
    });
    if (linkedToCrm) {
      actions.push({
        label: "Notes",
        icon: "/icons/folded-list.svg",
        onClick: onShowNotes,
      });
    }
    actions.push({
      label: "Exit",
      icon: "/icons/exit.svg",
      to: "/rebalances",
    });
    return actions;
  }, [
    readOnly,
    rebalance?.status,
    approve,
    reject,
    cancel,
    updateRebalance.isPending,
    showHistory,
    linkedToCrm,
    onShowNotes,
  ]);

  return typeof rebalance === "undefined" ||
    typeof household === "undefined" ? null : (
    <>
      <Row>
        <Col>
          <div className="d-flex justify-content-end mb-3 d-lg-none">
            <MobileActionsMenu actions={mobileActions} />
          </div>
          <ButtonToolbar className="d-none d-lg-flex mb-3 justify-content-end gap-2">
            {readOnly ? null : rebalance?.status === "proposed" ? (
              <ActionButton
                label="Save & Approve"
                icon="/icons/approve.svg"
                type="button"
                onClick={approve}
                disabled={updateRebalance.isPending}
              />
            ) : rebalance?.status === "approved" ? (
              <ActionButton
                label="Reject"
                icon="/icons/reject.svg"
                type="button"
                onClick={reject}
                disabled={updateRebalance.isPending}
              />
            ) : null}
            {readOnly ||
            !["requested", "proposed", "approved"].includes(
              rebalance?.status ?? "",
            ) ? null : (
              <ActionButton
                label="Cancel"
                icon="/icons/trash.svg"
                type="button"
                onClick={cancel}
                disabled={updateRebalance.isPending}
              />
            )}
            <ActionButton
              variant="secondary"
              icon="/icons/audit.svg"
              label="History"
              type="button"
              onClick={showHistory}
            />
            {!linkedToCrm ? null : (
              <ActionButton
                variant="secondary"
                icon="/icons/folded-list.svg"
                label="Notes"
                type="button"
                onClick={onShowNotes}
              />
            )}
            <ActionButton
              as={Link}
              to="/rebalances"
              label="Exit"
              icon="/icons/exit.svg"
            />
          </ButtonToolbar>
        </Col>
      </Row>
      {rebalance.type !== "position_swap" ? null : (
        // APP-1486, swap spec R84, R99: the proposal is read-only here; the
        // swap edits, approves and cancels it.
        <Alert variant="info">
          This trade proposal is part of a position swap. Change it from the
          swap.
          {rebalance.positionSwapId == null ? null : (
            <>
              {" "}
              <Link
                to={`/rebalances/position-swaps/${rebalance.positionSwapId}`}
              >
                Open the position swap
              </Link>
            </>
          )}
        </Alert>
      )}
      <Row className="mb-3">
        <Col
          xs={12}
          className="d-flex align-items-center justify-content-between flex-wrap gap-3"
        >
          <span className="fs-3 fw-semibold lh-1 text-wrap">
            <Link to={`/clients/households/${household.id}`}>
              {household.name}
            </Link>
          </span>
          {rebalance.status === "requested" ? null : (
            <div>
              <SummaryPill
                label="AUM"
                value={formatCurrency(rebalance.totalBalance)}
                size="sm"
                className="me-3 mb-3"
              />
              <SummaryPill
                label="Cash*"
                value={formatCurrency(rebalance.cashBalance)}
                size="sm"
                className="me-3 mb-3"
              />
              <SummaryPill
                label="Buy $"
                value={formatCurrency(buyTotal)}
                size="sm"
                className="me-3 mb-3"
              />
              <SummaryPill
                label="Sell $"
                value={`${formatCurrency(sellTotal)}`}
                size="sm"
                className="me-3 mb-3"
              />
              <SummaryPill
                label="Net $"
                value={`${formatCurrency(sellTotal - buyTotal)}`}
                size="sm"
              />
            </div>
          )}
        </Col>
        <Col xs={12} className="mt-0">
          <div className="small text-muted">
            *May exclude recent settlements
          </div>
        </Col>
      </Row>
      <Offcanvas show={isShowHistory} onHide={hideHistory} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Trade Proposal History</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <EntityAuditTrail
            history={rebalanceHistory}
            itemDisplay={renderRebalanceHistory}
            itemDisplayName="Trade Proposal"
            isLoading={isPendingRebalanceHistory}
            isError={isErrorRebalanceHistory}
          />
        </Offcanvas.Body>
      </Offcanvas>
      <NotesPane
        showNotes={showNotes}
        setShowNotes={setShowNotes}
        notes={dataNotes ?? []}
        onCreate={onCreateNote}
        isLoading={isPendingNotes}
        isError={isErrorNotes}
      />
    </>
  );
};

export default RebalanceHeader;
